import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { getDaypart, greeting } from "../lib/daypart";

export default function AdaptiveHero({ name, language, subline, rain }) {
  const reduce = useReducedMotion();
  const [dp, setDp] = useState(() => getDaypart());
  const [loaded, setLoaded] = useState(false);

  // re-check daypart on focus/resume and every 10 min
  useEffect(() => {
    const recheck = () => setDp(getDaypart());
    const iv = setInterval(recheck, 600000);
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => { clearInterval(iv); window.removeEventListener("focus", recheck); document.removeEventListener("visibilitychange", recheck); };
  }, []);

  useEffect(() => { setLoaded(false); }, [dp.key]);

  return (
    <div className="relative w-full h-[248px] sm:h-[300px] lg:h-[340px] overflow-hidden rounded-b-[34px] lg:rounded-[26px]" data-testid="adaptive-hero" data-daypart={dp.key}>
      {/* blur placeholder */}
      <img src={dp.blur} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover object-center scale-110" />
      {/* cross-dissolve + ken-burns photo */}
      <AnimatePresence mode="popLayout">
        <motion.img
          key={dp.key} src={dp.img} alt="" onLoad={() => setLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover object-center"
          initial={{ opacity: 0, scale: reduce ? 1 : 1.06 }}
          animate={{ opacity: loaded ? 1 : 0, scale: reduce ? 1 : 1 }}
          exit={{ opacity: 0 }}
          transition={{ opacity: { duration: 0.6 }, scale: { duration: 14, ease: "linear" } }}
        />
      </AnimatePresence>
      {/* readability scrim */}
      <div className="absolute inset-0" style={{ background: dp.scrim }} aria-hidden />
      {rain && <div className="absolute inset-0 bg-slate-900/25" aria-hidden />}

      {/* greeting */}
      <div className="absolute top-0 left-0 right-0 p-5 sm:p-7">
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }}
          className="font-display text-white font-extrabold leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] text-[26px] sm:text-[32px] lg:text-[36px]" data-testid="hero-greeting">
          {greeting(dp, name, language)}
        </motion.h1>
        {subline && (
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.28, duration: 0.5 }}
            className="text-white/95 text-sm sm:text-base mt-1 drop-shadow-[0_1px_4px_rgba(0,0,0,0.5)]">{subline}{rain ? " 🌧️" : ""}</motion.p>
        )}
      </div>
    </div>
  );
}
